// src/pages/const/DailyNew.jsx
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import FormContentManager from "../../components/common/form/FormContentManager";
import {
  getWorkReportDetail,
  getProjectDailyContext,
  getWorkReports, // 이전 공사일보 목록용
} from "../../api/const";

import DailyHeader from "../../components/const/Daily/DailyHeader";
import WorkNoteSection from "../../components/const/Daily/WorkNoteSection";
import EquipmentTable from "../../components/const/Daily/EquipmentTable";
import MaterialTable from "../../components/const/Daily/MaterialTable";
import PersonnelTable from "../../components/const/Daily/PersonnelTable";
import DailySummary from "../../components/const/Daily/DailySummary";
import HistoryDailyModal from "../../components/const/Daily/HistoryDailyModal";
import { getProjectName } from "../../api/project";

export default function DailyNew() {
  const navigate = useNavigate();
  const { id: projectId, dns } = useParams();
  const isEditMode = Boolean(dns);

  const [loading, setLoading] = useState(isEditMode);

  const [projectName, setProjectName] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const rawUser = localStorage.getItem("user");
  const user = rawUser ? JSON.parse(rawUser) : null;
  const [created_by, setCreatedBy] = useState(user?.name || "");

  const [reviewers, setReviewers] = useState([]);

  const [weather, setWeather] = useState("");
  const [tempMin, setTempMin] = useState("");
  const [tempMax, setTempMax] = useState("");
  const [tempAvg, setTempAvg] = useState("");
  const [precipAvg, setPrecipAvg] = useState("");
  const [precipTotal, setPrecipTotal] = useState("");
  const [snowAvg, setSnowAvg] = useState("");
  const [snowTotal, setSnowTotal] = useState("");
  const [windAvg, setWindAvg] = useState("");
  const [windMax, setWindMax] = useState("");

  const [todayWork, setTodayWork] = useState("");
  const [tomorrowWork, setTomorrowWork] = useState("");

  const [equipmentData, setEquipmentData] = useState([]);
  const [materialData, setMaterialData] = useState([]);
  const [personnelData, setPersonnelData] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);

  // 현황(자재/장비/인원) 데이터를 어디 기준 일보에서 가져올지 결정하는 dns
  // - 새 작성 페이지: 초기값 null
  // - 수정 페이지: 현재 문서 dns
  const [statusSourceDns, setStatusSourceDns] = useState(dns || null);

  // ====== 이전 작업 정보 불러오기 모달 상태 ======
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);

  // 공사일보 상세 응답 → 현재 폼 상태에 채워 넣는 공통 함수
  const fillFromDetail = useCallback((data) => {
    const meta = data.meta || {};

    setDate(meta.date || new Date().toISOString().slice(0, 10));
    setCreatedBy(data.created_by?.name || "");

    setWeather(meta.weather || "");
    setTempMin(meta.temp_min ?? "");
    setTempMax(meta.temp_max ?? "");
    setTempAvg(meta.temp_avg ?? "");
    setPrecipAvg(meta.precip_avg ?? "");
    setPrecipTotal(meta.precip_total ?? "");
    setSnowAvg(meta.snow_avg ?? "");
    setSnowTotal(meta.snow_total ?? "");
    setWindAvg(meta.wind_speed_avg ?? "");
    setWindMax(meta.wind_speed_max ?? "");

    setTodayWork(meta.today_work || "");
    setTomorrowWork(meta.tomorrow_work || "");
    setSelectedActivities(meta.activities || []);

    // ⭐ 조회 데이터 → today 필드 매핑은 기존 edit 모드 로직과 동일
    setEquipmentData(
      (data.equipments || []).map((e) => ({
        id: e.id,
        name: e.name,
        spec: e.spec,
        today: e.qty,
      }))
    );

    setMaterialData(
      (data.materials || []).map((m) => ({
        id: m.id,
        name: m.name,
        spec: m.spec,
        unit: m.unit,
        today: m.qty,
      }))
    );

    setPersonnelData(
      (data.personnels || []).map((p) => ({
        id: p.id,
        work_type: p.work_type,
        category: p.category,
        today: p.qty,
      }))
    );
  }, []);

  // ====== 이전 작업 정보 불러오기 버튼 동작 ======
  const openHistoryModal = async () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    try {
      const data = await getWorkReports({
        project: projectId,
        page_size: 50, // 필요 시 조정
      });
      // getWorkReports 응답 구조는 Daily 목록 페이지와 동일 (results/total) :contentReference[oaicite:8]{index=8}
      setHistoryRows(data.results ?? []);
    } catch (err) {
      console.error(err);
      alert("이전 공사일보 목록을 불러오지 못했습니다.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSelectHistory = async (row) => {
    try {
      const detail = await getWorkReportDetail(row.dns);

      // 현황 테이블들은 이 dns를 기준으로 자재/장비/인원 사용 현황을 다시 불러오도록 함
      setStatusSourceDns(row.dns);

      // 선택한 공사일보의 meta/작업내용/활동/현황 today 값들을 현재 폼에 채우기
      fillFromDetail(detail);

      setShowHistoryModal(false);
    } catch (err) {
      console.error(err);
      alert("이전 작업 정보를 불러오지 못했습니다.");
    }
  };

  /* ============================================
     EDIT MODE: 상세 불러오기
  ============================================= */
  useEffect(() => {
    if (!isEditMode) return;

    (async () => {
      try {
        const data = await getWorkReportDetail(dns);
        fillFromDetail(data); // 💡 상세 응답 → 상태 채우기

        const res = await getProjectName(projectId);
        setProjectName(res.name || "");
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [dns, isEditMode, projectId, fillFromDetail]);

  /* ============================================
     CREATE MODE: 초기 날씨 자동 로드
  ============================================= */
  useEffect(() => {
    if (isEditMode) return;

    (async () => {
      try {
        const ctx = await getProjectDailyContext(projectId);
        if (!ctx) return;

        setProjectName(ctx.pjt_nm ?? "");
        setWeather(ctx.summary || "");
        setTempMin(ctx.min_temp?.toFixed(1) ?? "");
        setTempMax(ctx.max_temp?.toFixed(1) ?? "");
        setTempAvg(ctx.avg_temp?.toFixed(1) ?? "");
        setPrecipAvg(ctx.precip_avg?.toFixed(1) ?? "");
        setPrecipTotal(ctx.precip_total?.toFixed(1) ?? "");
        setSnowAvg(ctx.snow_avg?.toFixed(1) ?? "");
        setSnowTotal(ctx.snow_total?.toFixed(1) ?? "");
        setWindAvg(ctx.wind_speed_avg?.toFixed(1) ?? "");
        setWindMax(ctx.wind_speed_max?.toFixed(1) ?? "");
      } catch (err) {
        console.warn(err);
      }
    })();
  }, [projectId, isEditMode]);

  /* ============================================
     FormContentManager → 저장용 getBody()
  ============================================= */
  const getBody = useCallback(() => {
    // 숫자형 기상 값: "" → null, 문자열/숫자는 Number로 변환
    const toNullableFloat = (v) =>
      v === "" || v === null || v === undefined ? null : Number(v);

    return {
      layout: null,
      values: {
        project: projectId,
        dns: dns || null,
        form_type: "daily",

        meta: {
          projectName,
          date,

          weather,
          temp_min: toNullableFloat(tempMin),
          temp_max: toNullableFloat(tempMax),
          temp_avg: toNullableFloat(tempAvg),
          precip_total: toNullableFloat(precipTotal),
          precip_avg: toNullableFloat(precipAvg),
          snow_total: toNullableFloat(snowTotal),
          snow_avg: toNullableFloat(snowAvg),
          wind_speed_avg: toNullableFloat(windAvg),
          wind_speed_max: toNullableFloat(windMax),

          today_work: todayWork,
          tomorrow_work: tomorrowWork,
          activities: selectedActivities,

          /* ================================
              백엔드 규격 100% 일치
          ================================= */
          equipments: equipmentData.map((e) => ({
            equipment: {
              name: e.name,
              spec: e.spec,
            },
            qty: Number(e.today) || 0,
          })),

          materials: materialData.map((m) => ({
            material: {
              name: m.name,
              spec: m.spec,
              unit: m.unit,
            },
            qty: Number(m.today) || 0,
          })),

          personnels: personnelData.map((p) => ({
            personnel: {
              work_type: p.work_type,
              category: p.category,
            },
            qty: Number(p.today) || 0,
          })),
        },
      },
    };
  }, [
    projectId,
    dns,
    projectName,
    date,
    weather,
    tempMin,
    tempMax,
    tempAvg,
    precipTotal,
    precipAvg,
    snowAvg,
    snowTotal,
    windAvg,
    windMax,
    todayWork,
    tomorrowWork,
    selectedActivities,
    equipmentData,
    materialData,
    personnelData,
  ]);
  console.log(reviewers)

  if (loading) return <div className="p-6">로딩 중...</div>;
  return (
    <>
      <FormContentManager
        params={{
          module: "const",
          project: projectId,
          doc_type: "daily",
          dns,
        }}
        docName={isEditMode ? "공사일보 수정" : "공사일보"}
        docDescribe="현장 공사일보를 작성합니다."
        getBody={getBody}
        redirectTo={`/project/${projectId}/const/daily`}
        reviewer={reviewers}
        headerExtraButtons={
          <button
            type="button"
            onClick={openHistoryModal}
            className="btn-basic"
          >
            이전 작업 정보 불러오기
          </button>
        }
        summaryComponent={
          <DailySummary
            projectName={projectName}
            date={date}
            author={created_by}
            reviewers={reviewers}
            weather={weather}
            tempMin={tempMin}
            tempMax={tempMax}
            tempAvg={tempAvg}
            precipAvg={precipAvg}
            precipTotal={precipTotal}
            snowAvg={snowAvg}
            snowTotal={snowTotal}
            windAvg={windAvg}
            windMax={windMax}
            todayWork={todayWork}
            tomorrowWork={tomorrowWork}
            equipmentData={equipmentData}
            materialData={materialData}
            personnelData={personnelData}
          />
        }
      >
        {/* ===== 실제 입력 UI ===== */}
        <DailyHeader
          projectName={projectName}
          dns={dns}
          date={date}
          setDate={setDate}
          author={created_by}
          reviewers={reviewers}
          setReviewers={setReviewers}
          weather={weather}
          tempMin={tempMin}
          tempMax={tempMax}
          tempAvg={tempAvg}
          precipTotal={precipTotal}
          precipAvg={precipAvg}
          snowTotal={snowTotal}
          snowAvg={snowAvg}
          windAvg={windAvg}
          windMax={windMax}
          setWeather={setWeather}
          setTempMin={setTempMin}
          setTempMax={setTempMax}
          setTempAvg={setTempAvg}
          setPrecipTotal={setPrecipTotal}
          setPrecipAvg={setPrecipAvg}
          setSnowTotal={setSnowTotal}
          setSnowAvg={setSnowAvg}
          setWindAvg={setWindAvg}
          setWindMax={setWindMax}
        />

        <h2 className="text-body-bold mt-6 mb-2">작업 사항</h2>
        <WorkNoteSection
          date={date}
          todayWork={todayWork}
          setTodayWork={setTodayWork}
          tomorrowWork={tomorrowWork}
          setTomorrowWork={setTomorrowWork}
          setSelectedActivities={setSelectedActivities}
          selectedActivities={selectedActivities}
        />
        <div className="space-y-4">
          <MaterialTable
            dailyId={statusSourceDns}
            project={projectId}
            date={date}
            onDataChange={setMaterialData}
          />

          <EquipmentTable
            dailyId={statusSourceDns}
            project={projectId}
            date={date}
            onDataChange={setEquipmentData}
          />

          <PersonnelTable
            dailyId={statusSourceDns}
            project={projectId}
            date={date}
            onDataChange={setPersonnelData}
          />
        </div>
      </FormContentManager>

      {/* ====== 이전 공사일보 선택 모달 ====== */}
      {showHistoryModal && (
        <HistoryDailyModal
          project={projectId}
          onClose={() => setShowHistoryModal(false)}
          onSelect={(row) => {
            handleSelectHistory(row);
            setShowHistoryModal(false);
          }}
        />
      )}
    </>
  );
}
